import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import merge from 'lodash.merge'
import { IApiPath, RequestConfig } from '../types'
import { ApiHttpPaths } from './ApiHttpPaths'

export class AxiosAPI {
  private axiosInstance: AxiosInstance


  constructor(domain?: string) {
    this.apiSchema = ApiHttpPaths
    this.apiDomain = this.parseDomain(domain)

    this.axiosInstance = axios.create()

    this.setupInterceptors()
  }

  apiSchema: typeof ApiHttpPaths
  apiDomain = ''
  private parseDomain = (domain?: string) => {

    if (!domain) {
      return window.location.origin
    }

    if (domain.startsWith('/')) {
      return `${window.location.origin}${domain}`
    }

    return domain
  }

  private getAuthToken = () => {
    let token = localStorage.getItem('authToken')

    if (!token) {
      token = ''
    }

    return token
  }

  private setDefaultHeaders = (options: RequestConfig = {}, path?: IApiPath): RequestConfig => {
    options.headers = {
      'Content-Type': 'application/json',
    }
    if (path && path.protected) {
      const token = this.getAuthToken()
      options.headers!.Authorization = `Bearer ${token}`
    }
    return options
  }

  private setupInterceptors = () => {
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (!error.response || error.code === 'ERR_NETWORK') {
          const customError = {
            ...error,
            message: 'Unable to connect to the server. Please check your connection and try again.',
            isConnectionError: true,
          }
          return Promise.reject(customError)
        }

        return Promise.reject(error)
      },
    )
  }

  public GET = (
    path: IApiPath,
    options: RequestConfig = {},
  ): Promise<AxiosResponse | AxiosError> => {
    const domain = this.apiDomain
    const defaultOptions: RequestConfig = this.setDefaultHeaders({
      timeout: 600000,
    }, path)

    return this.axiosInstance.get(`${domain}${path.url}`, merge(defaultOptions, options))
  }

  public PUT = <T>(
    path: IApiPath,
    body: any,
    options: RequestConfig,
  ): Promise<AxiosResponse<T> | AxiosError> => {
    const domain = this.apiDomain
    const defaultOptions: RequestConfig = this.setDefaultHeaders({
      timeout: 600000,
    }, path)

    return this.axiosInstance.put(`${domain}${path.url}`, body, merge(defaultOptions, options))
  }

  public POST = (
    path: IApiPath,
    body: any,
    options: RequestConfig = {},
  ): Promise<AxiosResponse | AxiosError> => {
    const domain =  this.apiDomain
    const axiosInstance = this.axiosInstance
    const defaultOptions: RequestConfig = this.setDefaultHeaders({
      timeout: 600000,
    }, path)

    return axiosInstance.post(`${domain}${path.url}`, body, merge(defaultOptions, options))
  }

  public DELETE = <T>(
    path: IApiPath,
    options: RequestConfig,
  ): Promise<AxiosResponse<T> | AxiosError> => {
    const domain = this.apiDomain
    const defaultOptions: RequestConfig = this.setDefaultHeaders({
      timeout: 600000,
    }, path)

    return this.axiosInstance.delete(`${domain}${path.url}`, merge(defaultOptions, options))
  }
}
